/**
 * XRCameraOverrideSystem — Disables CameraStack during XR presentation.
 *
 * Phase: Render, SubPhase: PreUpdate, Priority: -100
 * Runs before any other render-phase system to suppress the normal camera stack.
 * Three.js WebXRManager handles stereo rendering internally.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EngineRenderer } from '../../render/Renderer.js';
import type { CameraEntry } from '../../render/CameraStack.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';

export class XRCameraOverrideSystem implements ISystem {
  readonly name = 'XRCameraOverride';
  readonly phase = LoopPhase.Render;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = -100;

  private _renderer: EngineRenderer;
  private _savedEnabled = new Map<CameraEntry, boolean>();
  private _wasPresenting = false;

  constructor(_state: XRSessionState, renderer: EngineRenderer) {
    this._renderer = renderer;
  }

  execute(_dt: number, _world: World): void {
    const isPresenting = this._renderer.native.xr.isPresenting;

    // Transition: not presenting → presenting
    if (isPresenting && !this._wasPresenting) {
      this._disableCameraStack();
    }

    // Transition: presenting → not presenting
    if (!isPresenting && this._wasPresenting) {
      this._restoreCameraStack();
    }

    this._wasPresenting = isPresenting;
  }

  private _disableCameraStack(): void {
    const entries = this._renderer.cameras.getEntries();
    this._savedEnabled.clear();

    for (const entry of entries) {
      this._savedEnabled.set(entry, entry.enabled !== false);
      (entry as any).enabled = false;
    }
  }

  private _restoreCameraStack(): void {
    for (const [entry, wasEnabled] of this._savedEnabled) {
      (entry as any).enabled = wasEnabled;
    }
    this._savedEnabled.clear();
  }
}
